import { clsx } from "clsx";

import { BaseInputProps } from "./base";
import { getWrapperClassNames, inputClassNames } from "./class-names";
import { Label } from "../label";

export type NumberStepperProps = Pick<
  BaseInputProps,
  | "label"
  | "disabled"
  | "error"
  | "mandatory"
  | "id"
  | "message"
  | "onBlur"
  | "dataTestId"
> & {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  tooltip?: string;
};

export const NumberStepper = ({
  label,
  disabled,
  error,
  mandatory,
  id,
  message,
  onBlur,
  dataTestId,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  suffix,
  tooltip,
}: NumberStepperProps) => {
  const testId = dataTestId || "number-stepper";
  const atMinimum = value !== null && value <= min;
  const atMaximum = max !== undefined && value !== null && value >= max;

  const updateBy = (amount: number) => {
    const nextValue = (value ?? min) + amount;
    onChange(Math.min(max ?? Infinity, Math.max(min, nextValue)));
  };

  return (
    <Label
      text={label}
      mandatory={mandatory}
      disabled={disabled}
      name={id}
      error={error}
      message={message}
      tooltip={tooltip}
      tooltipTestId={`${testId}-label-tooltip`}
      className="block"
    >
      <div
        className={clsx(getWrapperClassNames({ error, disabled }), "w-full")}
        aria-disabled={disabled}
        data-testid={testId}
      >
        <input
          id={id}
          name={id}
          className={clsx(inputClassNames, "min-w-0")}
          value={value ?? ""}
          inputMode="numeric"
          pattern="\d*"
          disabled={disabled}
          aria-invalid={error}
          aria-errormessage={error ? message : undefined}
          data-testid={`${testId}-input`}
          onChange={(event) => {
            const digits = event.target.value.replace(/[^\d]/g, "");
            if (!digits) {
              onChange(null);
              return;
            }

            const parsed = Number.parseInt(digits, 10);
            onChange(Math.min(max ?? Infinity, parsed));
          }}
          onBlur={(event) => {
            if (value !== null && value < min) onChange(min);
            onBlur?.(event);
          }}
        />
        <button
          type="button"
          className="flex h-10 w-8 items-center justify-center text-ui text-neutral-600 focus-visible:outline-none disabled:cursor-not-allowed disabled:text-neutral-300"
          disabled={disabled || atMinimum}
          aria-label="Decrease value"
          data-testid={`${testId}-decrease`}
          onClick={() => updateBy(-step)}
        >
          &minus;
        </button>
        <button
          type="button"
          className="flex h-10 w-8 items-center justify-center text-ui text-neutral-600 focus-visible:outline-none disabled:cursor-not-allowed disabled:text-neutral-300"
          disabled={disabled || atMaximum}
          aria-label="Increase value"
          data-testid={`${testId}-increase`}
          onClick={() => updateBy(step)}
        >
          +
        </button>
        {suffix && (
          <span className="whitespace-nowrap text-ui text-neutral-600">
            {suffix}
          </span>
        )}
      </div>
    </Label>
  );
};
